import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { camelized_BundleConfigOptions_configurable_options_values } from 'src/queries/__fragments__/__generated__/BundleConfigOptions';
import {
    HandleBundleColorChangeType,
    HandleBundleSizeChangeType,
} from 'src/MixAndMatchBundle/MixAndMatchProductPicker';
import { MixAndMatchSelector } from 'src/MixAndMatchBundle/MixAndMatchSelector';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { useWearerBundles } from 'src/hooks/useWearerBundles';
import { ProductPickerSkeleton } from 'src/elements/Skeletons/Skeletons';
import { BundleTypeValues, BaseBundleType } from 'src/components/BundleProduct/types/index';
import { MixAndMatchVariant } from 'src/MixAndMatchBundle/MixAndMatchProductContainer';

import { ProductStockStatus } from '../../__generated__/globalTypes';

import classes from './MixAndMatchProductPicker.scss';

type Props = {
    colorDisabled?: boolean;
    sizeDisabled?: boolean;
    sizeOptions?: camelized_BundleConfigOptions_configurable_options_values[];
    colorOptions?: camelized_BundleConfigOptions_configurable_options_values[];
    handleBundleColorChange?: HandleBundleColorChangeType;
    handleBundleSizeChange?: HandleBundleSizeChangeType;
    index: number;
    productIndex?: number;
    productId: number;
    id: number;
    newOptionPicked?: string | undefined;
    setNewOptionPicked: Dispatch<SetStateAction<string | undefined>>;
    setInputEnabled: Dispatch<SetStateAction<boolean>>;
    setSizeEnabled?: Dispatch<SetStateAction<boolean>>;
    handleProductValueChange: (productId: number, value: number, type: 'colorId' | 'maxQty') => void;
    colorId?: number;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    setWearerBundleData: (data: BundleTypeValues | BaseBundleType) => void;
    variants: MixAndMatchVariant[];
};

export const MixAndMatchSizeAndColor: React.FC<Props> = ({
    handleBundleColorChange,
    handleBundleSizeChange,
    index,
    productIndex,
    productId,
    id,
    newOptionPicked,
    setNewOptionPicked,
    setInputEnabled,
    setSizeEnabled,
    handleProductValueChange,
    colorId,
    sizeOptions,
    colorOptions,
    colorDisabled,
    sizeDisabled,
    wearerOptions,
    summary,
    setWearerBundleData,
    variants,
}) => {
    const { array: wearerBundleData, loading } = useWearerBundles(wearerOptions, summary, productId);

    useEffect(() => {
        if (!loading && wearerBundleData) {
            setWearerBundleData(wearerBundleData[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    if (loading)
        return (
            <>
                <ProductPickerSkeleton className={classes.colourInput} />
                <ProductPickerSkeleton className={classes.sizeInput} />
            </>
        );

    const getOptions = (variant: camelized_BundleConfigOptions_configurable_options_values[] | undefined) => {
        const bundleVariants = (wearerBundleData[0] as BaseBundleType)?.bundleVariants;

        return variant?.filter((value) => {
            const computedVariants = bundleVariants?.filter(
                (bundleVariant) =>
                    bundleVariant?.attributes?.find((attribute) => attribute?.code === 'color')?.value_index ===
                    value.valueIndex,
            );

            const colorHasOneVariantInStock = computedVariants?.find(
                (computedVariant) => computedVariant?.product?.stock_status === ProductStockStatus.IN_STOCK,
            );

            if (colorHasOneVariantInStock) return value;
        });
    };

    const computedColorOptions =
        getOptions(variants?.find((variant) => variant.id === productId)?.value.colors) || colorOptions;

    return (
        <>
            <MixAndMatchSelector
                disabled={colorDisabled}
                productIndex={productIndex}
                title="Colour"
                options={computedColorOptions}
                handleBundleColorChange={handleBundleColorChange}
                index={index}
                className={classes.colourInput}
                productId={id}
                newOptionPicked={newOptionPicked}
                setNewOptionPicked={setNewOptionPicked}
                setInputEnabled={setInputEnabled}
                setSizeEnabled={setSizeEnabled}
                handleProductValueChange={handleProductValueChange}
            />
            <MixAndMatchSelector
                disabled={sizeDisabled}
                productIndex={productIndex}
                title="Size"
                options={sizeOptions}
                index={index}
                handleBundleSizeChange={handleBundleSizeChange}
                className={classes.sizeInput}
                productId={id}
                newOptionPicked={newOptionPicked}
                setNewOptionPicked={setNewOptionPicked}
                setInputEnabled={setInputEnabled}
                handleProductValueChange={handleProductValueChange}
                colorId={colorId}
                bundleDetails={wearerBundleData[0]}
            />
        </>
    );
};
